import _, { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { leadDataManagementEndpoint } from '../../../configs/endpoints';
import { useAuth } from '../../../hooks/useAuth';
import { ApiResponseType } from '../../../types';
import { SelectOption } from '../../../types/common';
import { client } from '../../../utils/axios-client';
import { OptionsStore } from '../../common/OptionsLoader';
import { ClaimType, ClaimValue } from '../../common/RenderIf';
import ExcelImportStore, { importSchema, SheetError } from './ExcelImportStore';
import { Lead, Session } from './LeadDataGridStore';

const useExcelImport = () => {
    const { user } = useAuth();
    const workbook = ExcelImportStore.useState((s) => s.workbook);
    const worksheet = ExcelImportStore.useState((s) => s.worksheet);
    const revalidate = ExcelImportStore.useState((s) => s.revalidate);
    const loaded = ExcelImportStore.useState((s) => s.loaded);
    const errors = ExcelImportStore.useState((s) => s.errors);
    const leads = ExcelImportStore.useState((s) => s.leads);
    const unmappedColumns = ExcelImportStore.useState((s) => s.unmappedColumns);
    const mapSchema = ExcelImportStore.useState((s) => s.mapSchema);
    const allColumns = ExcelImportStore.useState((s) => s.allColumns);
    const step = ExcelImportStore.useState((s) => s.step);
    const fileError = ExcelImportStore.useState((s) => s.fileError);
    const session = ExcelImportStore.useState((s) => s.session);
    const customers = OptionsStore.useState((s) => s.customers);
    const stages = OptionsStore.useState((s) => s.stages);
    const [customerSprints, setCustomerSprints] = useState<SelectOption[]>([]);
    const [sprintCadences, setSprintCadences] = useState<SelectOption[]>([]);
    const countries = OptionsStore.useState((s) => s.countries);
    const regions = OptionsStore.useState((s) => s.regions);
    const functions = OptionsStore.useState((s) => s.functions);
    const sources = OptionsStore.useState((s) => s.sources);
    const levels = OptionsStore.useState((s) => s.levels);
    const industries = OptionsStore.useState((s) => s.industries);
    const owners = OptionsStore.useState((s) => s.owners);

    useEffect(() => {
        let subscribed = true;

        if (unmappedColumns.length == 0 && loaded) {
            if (subscribed) {
                validateSheet();
            }
        }

        return () => {
            subscribed = false;
        };
    }, [loaded, workbook, worksheet, mapSchema, revalidate]);

    const loadWorkbook = (files: FileList) => {
        const reader = new FileReader();

        if (files && files.length !== 0) {
            reader.onload = (e) => {
                if (e.target) {
                    const data = e.target.result;
                    const tempWorkbook = XLSX.read(data, { type: 'array' });
                    const tempWorksheet = tempWorkbook.Sheets[tempWorkbook.SheetNames[0]];

                    const exceljsonObj = XLSX.utils.sheet_to_json(tempWorksheet, { defval: '' }) as object[];

                    const { mapSchema, unmappedColumns, tempErrors, columnNames, fileError } =
                        validateMappings(exceljsonObj);

                    ExcelImportStore.update((s) => {
                        s.workbook = tempWorkbook;
                        s.worksheet = tempWorksheet;
                        s.errors = tempErrors;
                        s.unmappedColumns = unmappedColumns;
                        s.mapSchema = mapSchema;
                        s.loaded = tempErrors.filter((s) => s.errorType === 'error').length === 0;
                        s.revalidate = !revalidate;
                        (s.allColumns = columnNames), (s.fileError = fileError);
                    });
                } else {
                    alert('error');
                }
            };

            reader.readAsArrayBuffer(files[0]);
        } else {
            alert('error');
        }
    };

    function containsWhitespace(str: string) {
        return /\s/.test(str);
    }

    const validateMappings = (sheet: object[]) => {
        let unmappedColumns = [] as string[];
        const mapSchema = structuredClone([...importSchema]);
        const tempErrors = [] as SheetError[];

        if (sheet.length === 0) {
            tempErrors.push({
                rowNum: 1,
                colNum: 1,
                message: 'file contains no records',
                errorType: 'error',
            });

            return { mapSchema, unmappedColumns, tempErrors, columnNames: [], fileError: true };
        }

        // get row header
        console.log('sheet', sheet[0]);
        const columnNames = Object.keys(sheet[0] as object);

        let fileError = false;

        for (let columnNumber = 0; columnNumber < columnNames.length; columnNumber++) {
            const columnName = columnNames[columnNumber];

            if (columnName === '__EMPTY') {
                tempErrors.push({
                    rowNum: 1,
                    colNum: columnNumber + 1,
                    message: `Column ${columnNumber} has no heading.  Please add the heading in your sheet and upload the file again.`,
                    errorType: 'error',
                });

                fileError = true;
            } else if (containsWhitespace(columnName)) {
                tempErrors.push({
                    rowNum: 1,
                    colNum: columnNumber + 1,
                    message: `Column ${
                        columnNumber + 1
                    } has a heading that contains spaces.  Please remove the spaces from the heading in your sheet and upload the file again.`,
                    errorType: 'error',
                });

                fileError = true;
            } else {
                const indexItem = mapSchema.find(
                    (s) => s.columnName.toLowerCase().replace(' ', '') === columnNames[columnNumber].toLowerCase(),
                );

                if (!indexItem) {
                    console.log('unmapped', columnName);
                    unmappedColumns.push(columnName);
                } else {
                    console.log(`mapped: ${columnName} to ${indexItem.columnName} `);
                    indexItem.mappedField = columnName;
                }
            }
        }

        if (fileError) {
            unmappedColumns = [];
        }

        return { mapSchema, unmappedColumns, tempErrors, columnNames, fileError };
    };

    const manualMapField = (mappedField: string, schemaColumn: string) => {
        ExcelImportStore.update((s, o) => {
            s.unmappedColumns = o.unmappedColumns.filter((s) => s != mappedField);

            const newSchema = structuredClone(o.mapSchema);
            const item = newSchema.find((s) => s.columnName == schemaColumn);

            if (item) {
                item.mappedField = mappedField;
            }

            s.mapSchema = newSchema;
        });
    };

    const saveRecords = async () => {
        const response = await client.post<ApiResponseType>(leadDataManagementEndpoint + '/save', { records: leads });

        if (response.data.succeeded) {
            console.log('saved');
        } else {
            alert('error');
        }
    };

    const validateSheet = () => {
        if (!worksheet) {
            throw 'worksheet not loaded';
        }

        const exceljsonObj = XLSX.utils.sheet_to_json(worksheet) as any[];
        const tempLeads = [] as Lead[];
        const tempErrors = [] as SheetError[];

        for (let rowIndex = 0; rowIndex < exceljsonObj.length; rowIndex++) {
            const data = exceljsonObj[rowIndex];

            const newLead = {
                id: crypto.randomUUID(),
                firstName: '',
                lastName: '',
                emailAddress: '',
                jobTitle: '',
                salutation: '',
                company: '',
                sessionId: session.sessionId,
            } as Lead;

            for (let columnIndex = 0; columnIndex < mapSchema.length; columnIndex++) {
                const col = mapSchema[columnIndex];

                if (col.mappedField) {
                    const value = data[col.mappedField];

                    if (col.isSelect && isEmpty(value)) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'error',
                            message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                        });
                    } else if (value && typeof value === 'string' && value.toString().length > col.maxLength) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'error',
                            message: `The value in column ${col.mappedField} exceeds the maximum length of ${col.maxLength}. Please fix this in your sheet and upload the file again. `,
                        });
                    } else if (!value) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'warning',
                            message: `Missing value for column ${col.mappedField}`,
                        });
                    } else {
                        if (col.columnName.toLowerCase() === 'country') {
                            const option = countries.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.countryId = option.value;
                                newLead.countryName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadregion') {
                            const option = regions.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.addressRegionId = option.value;
                                newLead.addressRegionName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadindustry') {
                            const option = industries.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.industryId = option.value;
                                newLead.industryName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadfunction') {
                            const option = functions.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.leadFunctionId = option.value;
                                newLead.leadFunctionName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadsource') {
                            const option = sources.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.leadSourceId = option.value;
                                newLead.leadSourceName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadlevel') {
                            const option = levels.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newLead.leadLevelId = option.value;
                                newLead.leadLevelName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadowner') {
                            const option = owners.find((p) => p.name.toLowerCase() === value.toLowerCase());

                            if (option) {
                                newLead.leadOwnerId = option.value;
                                newLead.leadOwnerName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else {
                            newLead[col.columnName] = value;
                        }
                    }
                }
            }

            tempLeads.push(newLead);
        }

        ExcelImportStore.update((s, o) => {
            s.errors = tempErrors;
            s.leads = [...tempLeads];
        });
    };

    const nextStep = () => {
        ExcelImportStore.update((s, o) => {
            s.step = o.step + 1;
        });
    };

    const onSave = async () => {
        let hasError = false;
        ExcelImportStore.update((s) => {
            s.session.nameError = false;
            s.session.startDateError = false;
            s.session.cadenceError = false;
            s.session.customerError = false;
            s.session.sprintError = false;
        });

        if (_.isEmpty(session.name)) {
            ExcelImportStore.update((s) => {
                s.session.nameError = true;
            });

            hasError = true;
        }

        if (_.isEmpty(session.customerId)) {
            ExcelImportStore.update((s) => {
                s.session.customerError = true;
            });

            hasError = true;
        }

        if (session.importType === 'ImportToSprint') {
            if (_.isEmpty(session.cadenceId)) {
                ExcelImportStore.update((s) => {
                    s.session.cadenceError = true;
                });

                hasError = true;
            }

            if (_.isEmpty(session.sprintId)) {
                ExcelImportStore.update((s) => {
                    s.session.sprintError = true;
                });

                hasError = true;
            }

            if (_.isEmpty(session.startDate)) {
                ExcelImportStore.update((s) => {
                    s.session.startDateError = true;
                });

                hasError = true;
            }
        }

        if (hasError) return;

        const response = await client.post<ApiResponseType>(leadDataManagementEndpoint + '/savesession', session);

        return response.data.succeeded;
    };

    const resetImport = () => {
        if (!user) return null;

        let step = 0;

        if (
            !user.claims.some(
                (roleClaim) => roleClaim.value === ClaimValue.ImportExcel && roleClaim.claim === ClaimType.LeadImport,
            ) &&
            !user.isAdmin
        )
            step = 1;

        ExcelImportStore.update((s) => {
            s.workbook = null;
            s.worksheet = null;
            s.errors = [];
            s.unmappedColumns = [];
            s.mapSchema = [];
            s.loaded = false;
            s.allColumns = [];
            s.leads = [];
            s.step = step;
            (s.session = {
                importType: 'ImportToSprint',
                name: '',
                sessionId: crypto.randomUUID(),
            } as Session),
                (s.fileError = false);
        });
    };

    const resetResearchImport = () => {
        if (!user) return null;

        let step = 1;

        if (
            !user.claims.some(
                (roleClaim) => roleClaim.value === ClaimValue.ImportExcel && roleClaim.claim === ClaimType.LeadImport,
            ) &&
            !user.isAdmin
        )
            step = 1;

        ExcelImportStore.update((s) => {
            s.workbook = null;
            s.worksheet = null;
            s.errors = [];
            s.unmappedColumns = [];
            s.mapSchema = [];
            s.loaded = false;
            s.allColumns = [];
            s.leads = [];
            s.step = step;
            (s.session = {
                importType: 'ImportToSprint',
                name: '',
                sessionId: crypto.randomUUID(),
            } as Session),
                (s.fileError = false);
        });
    };

    const skip = () => {
        if (!user) return false;
        const canSkip =
            !user.claims.some(
                (roleClaim) => roleClaim.value === ClaimValue.ManualImport && roleClaim.claim === ClaimType.LeadImport,
            ) || user.isAdmin;

        if (canSkip) {
            ExcelImportStore.update((s) => {
                s.workbook = null;
                s.worksheet = null;
                s.errors = [];
                s.unmappedColumns = [];
                s.mapSchema = [];
                s.loaded = false;
                s.allColumns = [];
                s.leads = [];
                s.step = 1;
                (s.session = {
                    importType: 'ImportToSprint',
                    name: '',
                    sessionId: crypto.randomUUID(),
                } as Session),
                    (s.fileError = false);
            });
        }
    };

    return {
        skip,
        resetResearchImport,
        resetImport,
        saveRecords,
        onSave,
        setCustomerSprints,
        setSprintCadences,
        customers,
        stages,
        customerSprints,
        sprintCadences,
        nextStep,
        workbook,
        session,
        loadWorkbook,
        loaded,
        fileError,
        unmappedColumns,
        hasUnmapped: unmappedColumns.length > 0,
        errors,
        hasWarning: errors.filter((s) => s.errorType === 'warning').length > 0,
        hasError: errors.filter((s) => s.errorType === 'error').length > 0,
        mapSchema,
        manualMapField,
        allColumns,
        step,
    };
};

export default useExcelImport;
